import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, toApiError } from './api';
import {
  enqueueAction,
  type QueuedAction,
  type QueuedActionDraft,
  readQueue,
  removeAction,
  setActionFailure,
} from './offline-queue';

type OfflineContextValue = {
  online: boolean;
  actions: QueuedAction[];
  sending: boolean;
  /** Records what the person did and sends it when there is a connection. */
  take: (draft: QueuedActionDraft) => QueuedAction;
  send: () => Promise<{ sent: number; failed: number }>;
  discard: (clientRequestId: string) => void;
  retry: (clientRequestId: string) => void;
};

const OfflineContext = createContext<OfflineContextValue | null>(null);

/**
 * A refusal the server will give again for the same reason is kept on the
 * action and shown to the person, rather than retried forever. A transport
 * failure is not one of those: it leaves the action untouched, because the
 * write may well have committed and the key is what makes resending safe.
 */
function isRepeatableRefusal(error: unknown): boolean {
  const { code } = toApiError(error);
  return code !== 'NETWORK' && code !== 'INTERNAL_SERVER_ERROR';
}

export function OfflineProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [actions, setActions] = useState<QueuedAction[]>(() => readQueue());
  const [online, setOnline] = useState(() => navigator.onLine);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);

  const send = useCallback(async () => {
    if (inFlight.current) return { sent: 0, failed: 0 };
    inFlight.current = true;
    setSending(true);
    let sent = 0;
    let failed = 0;
    try {
      for (const action of readQueue()) {
        if (action.failure) continue;
        try {
          if (action.kind === 'fulfill') {
            await api.donation.fulfill({
              id: action.donationId,
              clientRequestId: action.clientRequestId,
              ...action.input,
            });
          } else {
            await api.donation.queue({
              id: action.donationId,
              clientRequestId: action.clientRequestId,
              ...action.input,
            });
          }
          setActions(removeAction(action.clientRequestId));
          sent += 1;
        } catch (error) {
          if (!isRepeatableRefusal(error)) break;
          setActions(setActionFailure(action.clientRequestId, toApiError(error).code));
          failed += 1;
        }
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
    if (sent > 0) await queryClient.invalidateQueries({ queryKey: ['donations'] });
    return { sent, failed };
  }, [queryClient]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void send();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [send]);

  const value = useMemo<OfflineContextValue>(
    () => ({
      online,
      actions,
      sending,
      take: (draft) => {
        const action = enqueueAction(draft);
        setActions(readQueue());
        if (navigator.onLine) void send();
        return action;
      },
      send,
      discard: (clientRequestId) => setActions(removeAction(clientRequestId)),
      retry: (clientRequestId) => {
        setActions(setActionFailure(clientRequestId, null));
        if (navigator.onLine) void send();
      },
    }),
    [online, actions, sending, send],
  );

  return <OfflineContext value={value}>{children}</OfflineContext>;
}

export function useOffline(): OfflineContextValue {
  const context = use(OfflineContext);
  if (!context) throw new Error('useOffline must be used inside OfflineProvider');
  return context;
}
